import type { ComponentProps, ReactNode } from "react";
import { cn } from "@ascleo/ui/lib/utils";

/** Icon-chip tints for {@link AltCard}, mirroring the kiosk tile families. */
const ALT_ICON_TONES = {
  sky: "bg-brand-supporting-subtle text-brand-supporting-ink",
  orange: "bg-brand-accent-subtle text-brand-accent-ink",
  primary: "bg-primary-subtle text-primary-ink",
  neutral: "bg-foreground-subtle text-foreground",
} as const;

export type AltCardIconTone = keyof typeof ALT_ICON_TONES;

export interface AltCardProps
  extends Omit<ComponentProps<"button">, "title" | "children"> {
  /** Icon element (lucide / `@ascleo/icons`); sized to 22px automatically. */
  icon?: ReactNode;
  /** Tint of the icon chip; defaults to the supporting (sky) family. */
  iconTone?: AltCardIconTone;
  /** Primary line of the action. */
  title: ReactNode;
  /** Optional supporting line. */
  desc?: ReactNode;
}

/**
 * A secondary "other ways" action row used across kiosk steps: an icon chip
 * plus a title and description, styled as a quiet card that highlights on hover.
 * Mirrors the prototype's `.alt-card`.
 *
 * @example
 * ```tsx
 * <AltCard
 *   icon={<UserSearch />}
 *   title={t("self-service.check-in.card-check.no-card-action")}
 *   desc={t("self-service.check-in.card-check.no-card-hint")}
 *   data-testid="no-card-button"
 *   onClick={handleNoCard}
 * />
 * ```
 */
export function AltCard({
  icon,
  iconTone = "sky",
  title,
  desc,
  className,
  type = "button",
  ...props
}: AltCardProps) {
  return (
    <button
      type={type}
      className={cn(
        "flex items-center gap-4 rounded-2xl border bg-card px-[18px] py-4 text-left transition-colors hover:border-primary-vivid hover:bg-primary-soft disabled:pointer-events-none disabled:opacity-50",
        className,
      )}
      {...props}
    >
      {icon && (
        <span
          aria-hidden
          className={cn(
            "grid size-11 shrink-0 place-items-center rounded-[12px] [&_svg]:size-[22px]",
            ALT_ICON_TONES[iconTone],
          )}
        >
          {icon}
        </span>
      )}
      <span className="min-w-0">
        <span className="block text-[15px] leading-[1.25] font-medium">
          {title}
        </span>
        {desc && (
          <span className="mt-0.5 block text-[13px] text-muted-foreground">
            {desc}
          </span>
        )}
      </span>
    </button>
  );
}
